import type { TFunction } from "i18next";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem, ToggleGroupSeparator } from "@/components/ui/toggle-group";
import { NAMESPACES } from "@/i18n/constants";
import type { DurationSource } from "@/services/analytics/events";
import { EVENT_DURATION_LOWER_BOUND_MS, EVENT_DURATION_UPPER_BOUND_MS } from "../on-demand-irrigation-constants";
import { BASE_UNIT_VALUE, TOGGLE_GROUP_ACTIVE_CLASS } from "./form-constants";
import { FormValidationMessage } from "./form-validation-message";
import type { DurationPreset, PresetTimeUnitKey } from "./irrigation-presets";

function validateCustomDuration(t: TFunction, input: string): string | null {
    const durationNumber = Number(input);
    if (!Number.isFinite(durationNumber)) return t("duration-not-numeric");
    if (durationNumber <= 0) return t("duration-must-be-positive");
    if (durationNumber * BASE_UNIT_VALUE < EVENT_DURATION_LOWER_BOUND_MS)
        return t("duration-too-short", { minDurationMinutes: EVENT_DURATION_LOWER_BOUND_MS / minuteMs });
    if (durationNumber * BASE_UNIT_VALUE > EVENT_DURATION_UPPER_BOUND_MS)
        return t("duration-too-long", { maxDurationHours: EVENT_DURATION_UPPER_BOUND_MS / hourMs });
    return null;
}

interface DurationPickerProps {
    onDurationChange: (durationMs: number, error: string | null, durationSource: DurationSource) => void;
    presets: DurationPreset[];
}

export function DurationPicker({ onDurationChange, presets }: DurationPickerProps) {
    const [selectedPreset, setSelectedPreset] = useState<number | null>(() => presets[0]?.value ?? null);
    const [customInput, setCustomInput] = useState<string>("");

    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.create-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });

    const customEntered = customInput !== "";
    const customError = customEntered ? validateCustomDuration(tOnDemandValidationErrors, customInput) : null;
    const parsedMinutes = customEntered ? Number(customInput) : 0;
    const customMs = customError === null && parsedMinutes > 0 ? parsedMinutes * BASE_UNIT_VALUE : 0;
    const durationMs = customEntered ? customMs : (selectedPreset ?? 0);
    const durationSource: DurationSource = customEntered ? "custom" : "preset";

    useEffect(() => {
        onDurationChange(durationMs, customError, durationSource);
    }, [durationMs, customError, durationSource, onDurationChange]);

    const onClickPreset = (value: number) => {
        setSelectedPreset(value);
        setCustomInput("");
    };

    const presetToggleLabel = (amount: number, unit: PresetTimeUnitKey) =>
        unit === "hours"
            ? t("duration.preset-label-hours", { amount })
            : t("duration.preset-label-minutes", { amount });

    return (
        <div className={"flex flex-col gap-1"}>
            <p id={"duration-label"} className={"text-sm"}>
                {t("duration.title")}
            </p>
            <div className={"flex items-center flex-row gap-2"}>
                <ToggleGroup
                    size={"lg"}
                    aria-labelledby={"duration-label"}
                    variant={"outline"}
                    value={selectedPreset !== null ? [String(selectedPreset)] : []}
                    className={TOGGLE_GROUP_ACTIVE_CLASS}
                >
                    {presets.map((option, index) => (
                        <Fragment key={option.value}>
                            {index > 0 && <ToggleGroupSeparator />}
                            <ToggleGroupItem
                                value={String(option.value)}
                                onClick={() => onClickPreset(option.value)}
                                className={"text-sm xs:text-base"}
                            >
                                {presetToggleLabel(option.amount, option.unit)}
                            </ToggleGroupItem>
                        </Fragment>
                    ))}
                </ToggleGroup>
                <Input
                    size={"lg"}
                    className={`w-full ${customEntered ? "border-primary" : ""}`}
                    placeholder={t("duration.placeholder")}
                    type={"number"}
                    value={customInput}
                    onChange={(e) => {
                        setCustomInput(e.target.value);
                        setSelectedPreset(null);
                    }}
                />
            </div>
            {customError && <FormValidationMessage errorMessage={customError} />}
        </div>
    );
}
